import React, { useCallback, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import TuneRoundedIcon from '@material-ui/icons/TuneRounded';
import strings from 'strings';
import { TextBox } from 'UI/components/atoms';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import { Endpoints } from 'UI/constants/endpoints';
import {
  idOptionSelected,
  industrySpecialtyOptionLabel,
  REQUIRED_VALIDATION,
  titleOptionLabel
} from 'UI/utils';

import { ROLEPLAY_EXPERIENCE_OPTIONS } from '../constants';

export const COPIES = strings.ai.generators.roleplay;

export const RoleplayProfileFields = ({
  disabled = false,
  formFieldsMap,
  labelPrefix = '',
  showExperienceField = true
}) => {
  const isSubspecialtyRequiredRef = useRef(false);
  const { control, errors, register, setValue, watch, clearError } = useFormContext();
  const specialtyValue = watch(formFieldsMap.Specialty.key);

  const withLabelPrefix = label => (labelPrefix ? `${labelPrefix} ${label}` : label);

  const handleSelectChange = ({ dependentFields = null, value }) => {
    if (dependentFields) {
      const fields = Array.isArray(dependentFields) ? dependentFields : [dependentFields];
      fields.forEach(field => setValue(field, null));

      if (fields.includes(formFieldsMap.Subspecialty.key)) {
        isSubspecialtyRequiredRef.current = false;
        clearError && clearError(formFieldsMap.Subspecialty.key);
      }
    }

    return value;
  };

  const subspecialtyUrl = specialtyValue
    ? Endpoints.Subspecialties.replace(':id', specialtyValue.id)
    : null;

  const handleSubspecialtiesLoaded = useCallback(options => {
    isSubspecialtyRequiredRef.current = Array.isArray(options) && options.length > 0;
  }, []);

  return (
    <>
      <Box alignItems="center" display="flex" flexDirection="row" gridGap={8} mb={2}>
        <TuneRoundedIcon />
        <Typography variant="subtitle1">{COPIES.form.sections.roleplayProfile.title}</Typography>
      </Box>

      <Controller
        as={
          <AutocompleteSelect
            disabled={disabled}
            error={!!errors[formFieldsMap.Specialty.key]}
            errorText={errors[formFieldsMap.Specialty.key]?.message}
            getOptionLabel={industrySpecialtyOptionLabel}
            getOptionSelected={idOptionSelected}
            groupBy={option => option.industry_title}
            name={formFieldsMap.Specialty.key}
            placeholder={COPIES.form.sections.roleplayProfile.labels.industrySpecialty}
            renderOption={titleOptionLabel}
            url={Endpoints.Specialties}
          />
        }
        control={control}
        defaultValue={null}
        name={formFieldsMap.Specialty.key}
        onChange={([, value]) =>
          handleSelectChange({ dependentFields: [formFieldsMap.Subspecialty.key], value })
        }
        onChangeName="onSelect"
        rules={REQUIRED_VALIDATION}
        valueName="selectedValue"
      />

      <Controller
        as={
          <AutocompleteSelect
            disabled={disabled}
            error={isSubspecialtyRequiredRef.current && !!errors[formFieldsMap.Subspecialty.key]}
            errorText={
              isSubspecialtyRequiredRef.current
                ? errors[formFieldsMap.Subspecialty.key]?.message
                : undefined
            }
            name={formFieldsMap.Subspecialty.key}
            onOptionsLoaded={handleSubspecialtiesLoaded}
            placeholder={COPIES.form.sections.roleplayProfile.labels.subspecialty}
            renderOption={titleOptionLabel}
            url={subspecialtyUrl}
          />
        }
        control={control}
        defaultValue={null}
        name={formFieldsMap.Subspecialty.key}
        onChange={([, value]) => handleSelectChange({ value })}
        onChangeName="onSelect"
        rules={{
          validate: value => {
            if (!isSubspecialtyRequiredRef.current) return true;
            return Boolean(value) || REQUIRED_VALIDATION.required;
          }
        }}
        valueName="selectedValue"
      />

      <TextBox
        disabled={disabled}
        error={!!errors[formFieldsMap.Title.key]}
        helperText={errors[formFieldsMap.Title.key]?.message}
        inputRef={register(REQUIRED_VALIDATION)}
        label={withLabelPrefix(COPIES.form.sections.roleplayProfile.labels.title)}
        name={formFieldsMap.Title.key}
        placeholder={withLabelPrefix(COPIES.form.sections.roleplayProfile.labels.title)}
      />

      {showExperienceField && (
        <Controller
          as={
            <AutocompleteSelect
              defaultOptions={ROLEPLAY_EXPERIENCE_OPTIONS}
              disabled={disabled}
              error={!!errors[formFieldsMap.Experience.key]}
              errorText={errors[formFieldsMap.Experience.key]?.message}
              name={formFieldsMap.Experience.key}
              placeholder={withLabelPrefix(COPIES.form.sections.roleplayProfile.labels.experience)}
            />
          }
          control={control}
          defaultValue={null}
          name={formFieldsMap.Experience.key}
          onChange={([, value]) => handleSelectChange({ value })}
          onChangeName="onSelect"
          rules={REQUIRED_VALIDATION}
          valueName="selectedValue"
        />
      )}
    </>
  );
};
